import React, { useEffect } from 'react';
import ModalPortal from "../../../components/common/ModalPortal";
import { Button } from '../../../components/common/Button';
import { PhoneOff } from 'lucide-react';

interface EndInterviewConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * EndInterviewConfirmationModal - Modal for confirming interview end
 * 
 * This component is responsible for:
 * 1. Displaying a confirmation dialog when ending an interview
 * 2. Providing confirm/cancel actions
 * 3. Ensuring media tracks are preserved during modal display
 */
const EndInterviewConfirmationModal: React.FC<EndInterviewConfirmationModalProps> = ({ 
  isOpen, 
  onClose,
  onConfirm
}) => {
  // Prevent modal from affecting camera stream
  useEffect(() => {
    if (isOpen) {
      // When modal opens, ensure all media tracks maintain their state
      const maintainMediaTracks = () => {
        const videoEl = document.querySelector('video');
        if (videoEl && videoEl.srcObject instanceof MediaStream) {
          const mediaStream = videoEl.srcObject;
          mediaStream.getTracks().forEach(track => {
            // Don't change the state, just ensure track properties are preserved
            console.log(`Preserving ${track.kind} track state: ${track.enabled ? 'enabled' : 'disabled'}`);
          });
        }
      };
      
      maintainMediaTracks();
    }
  }, [isOpen]);
  
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  if (!isOpen) return null;
  
  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in z-[100]">
        <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-xl max-w-md w-full border border-border-light dark:border-border-dark animate-fade-in-up">
          <div className="p-5 border-b border-border-light dark:border-border-dark">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-red-500/10 mr-3">
                <PhoneOff className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
                End Interview
              </h3>
            </div>
          </div>
          <div className="p-5">
            <p className="text-base text-text-secondary-light dark:text-text-secondary-dark mb-4">
              Are you sure you want to end this interview? Your session will be analyzed and feedback will be generated.
            </p>

            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={onClose}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={onConfirm}
                className="w-full sm:w-auto !bg-red-500 hover:!bg-red-600 !border-red-500 !shadow-red-500/30"
              >
                Yes, End Interview
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default EndInterviewConfirmationModal; 